import { Button, HStack, NativeSelect, VStack } from '@chakra-ui/react'
import React, { useCallback, useEffect, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import { Field, Modal } from '@mangovoice/mango-ui'

import type { PhoneNumberDetail } from '../api/messagingApi.types'
import type { MessagingTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'

export interface AssociatedSmsNumbersModalProps {
  isOpen: boolean
  onClose: () => void
  availableNumbers: PhoneNumberDetail[]
  isLoading: boolean
  onSubmit: (phoneNumberId: number) => void
  isSubmitting?: boolean
  toast: ToastLike
  /** Optional translation function for i18n; when omitted, components use fallback English */
  t?: MessagingTranslateFn
}

const formatPhoneDisplay = (number: string): string => {
  if (number.length === 11 && number.startsWith('1')) {
    return `(${number.slice(1, 4)}) ${number.slice(4, 7)}-${number.slice(7)}`
  }
  return number
}

export const AssociatedSmsNumbersModal: React.FC<
  AssociatedSmsNumbersModalProps
> = ({
  isOpen,
  onClose,
  availableNumbers,
  isLoading,
  onSubmit,
  isSubmitting = false,
  toast,
  t,
}) => {
  const [selectedId, setSelectedId] = useState<string>('')

  useEffect(() => {
    if (isOpen) {
      setSelectedId('')
    }
  }, [isOpen])

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!selectedId) {
        toast.create({
          title: text(
            t,
            'messaging.smsModal.validationErrorTitle',
            'Validation Error',
          ),
          description: text(
            t,
            'messaging.smsModal.validationErrorDescription',
            'Please select an SMS number',
          ),
          type: 'error',
          duration: 3000,
          closable: true,
        })
        return
      }
      onSubmit(parseInt(selectedId, 10))
    },
    [selectedId, onSubmit, toast, t],
  )

  return (
    <Modal
      open={isOpen}
      onOpenChange={(e) => !e.open && onClose()}
      title={text(t, 'messaging.smsModal.title', 'Associated SMS Numbers')}
      maxWidth="500px"
      footer={
        <HStack gap={3} w="full">
          <Button
            borderRadius="lg"
            variant="outline"
            onClick={onClose}
            flex={1}
          >
            {text(t, 'messaging.smsModal.close', 'Close')}
          </Button>
          <Button
            type="submit"
            form="associated-sms-numbers-form"
            borderRadius="lg"
            colorPalette="warning"
            disabled={isSubmitting || !selectedId}
            flex={1}
          >
            {isSubmitting
              ? text(t, 'messaging.smsModal.submitting', 'Submitting...')
              : text(t, 'messaging.smsModal.submit', 'Submit')}
          </Button>
        </HStack>
      }
    >
      <form id="associated-sms-numbers-form" noValidate onSubmit={handleSubmit}>
        <VStack gap={5} align="stretch">
          <Field.Root required>
            <Field.Label>
              {text(t, 'messaging.smsModal.label', 'Associated SMS Numbers')}
            </Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                _disabled={{ opacity: 0.5 }}
              >
                <option value="">
                  {isLoading
                    ? text(t, 'messaging.smsModal.loading', 'Loading...')
                    : text(
                        t,
                        'messaging.smsModal.selectPlaceholder',
                        'Select a number',
                      )}
                </option>
                {availableNumbers.map((num) => (
                  <option key={num.id} value={num.id}>
                    {formatPhoneDisplay(num.number)}{' '}
                    {num.name ? `(${num.name})` : ''}
                  </option>
                ))}
              </NativeSelect.Field>
            </NativeSelect.Root>
          </Field.Root>
        </VStack>
      </form>
    </Modal>
  )
}
